import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import Logo from "@/components/founding/Logo";
import HomeLogoLink from "@/components/founding/HomeLogoLink";
import Footer from "@/components/founding/Footer";
import LegalDisclaimerModal from "@/components/founding/LegalDisclaimerModal";
import { getClientId } from "@/lib/clientId";
import { apiBase } from "@/lib/apiBase";
import {
    User,
    ShieldCheck,
    CheckCircle2,
    ArrowRight,
    Loader2,
} from "lucide-react";

const API = apiBase();

const PLANS = [
    {
        key: "founding_beta",
        label: "Founding Beta",
        price: "$249/month",
        sub: "Monthly subscription · 15 SRUs each month",
        bullets: ["Real AI processing", "15 SRUs included each month", "Submit unlimited drawings — limits apply only to exports", "Lifetime Founding badge"],
    },
    {
        key: "payg",
        label: "Pay-As-You-Go",
        price: "Usage-based",
        sub: "Monthly cap default $100",
        bullets: ["Real AI processing", "Unlimited projects", "Default $100/mo cap"],
    },
];

export default function Gateway() {
    const [step, setStep] = useState(1);
    const [profile, setProfile] = useState({ name: "", email: "", firm: "", license: "" });
    const [plan, setPlan] = useState("founding_beta");
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        // Resume gateway state
        (async () => {
            try {
                const me = await axios.get(`${API}/billing/me`, {
                    headers: { "X-Client-Id": getClientId() },
                });
                if (me.data.profile) {
                    setStep(2);
                    setProfile({
                        name: me.data.profile.name || "",
                        email: me.data.profile.email || "",
                        firm: me.data.profile.firm || "",
                        license: me.data.profile.license || "",
                    });
                }
            } catch (e) {
                /* ignore */
            }
        })();
    }, []);

    const submitProfile = async (e) => {
        e.preventDefault();
        if (!profile.name.trim() || !profile.email.trim()) {
            toast.error("Name and email are required.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/billing/profile`, profile, {
                headers: { "X-Client-Id": getClientId() },
            });
            toast.success("Profile saved");
            setStep(2);
        } catch (err) {
            toast.error(err?.response?.data?.detail || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    const startCheckout = async (e) => {
        e.preventDefault();
        if (plan !== "founding_beta") {
            toast.error("This plan is not available in Stripe checkout yet.");
            return;
        }
        setSubmitting(true);
        try {
            const response = await axios.post(`${API}/payments/v1/checkout/session`, {
                package_id: plan,
                origin_url: window.location.origin,
                client_id: getClientId(),
            });
            if (!response.data?.checkout_url) {
                throw new Error("No checkout URL returned from server");
            }
            window.location.href = response.data.checkout_url;
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : err.message);
            setSubmitting(false);
        }
    };

    return (
        <main className="min-h-screen bg-black text-slate-100 flex flex-col" data-testid="gateway-page">
            <header className="sticky top-0 z-40 w-full bg-black/90 backdrop-blur border-b border-white/10">
                <div className="mx-auto max-w-[1200px] px-6 lg:px-10 h-16 flex items-center justify-between">
                    <HomeLogoLink
                        testId="gateway-home-logo-link"
                        className="transition-opacity duration-200 hover:opacity-80"
                    >
                        <Logo size={30} />
                    </HomeLogoLink>
                    <span className="fsrs-label text-slate-500">
                        REAL-MODE&nbsp;GATEWAY
                    </span>
                    <Link to="/studio" className="fsrs-cta-ghost text-lg py-2 px-3">
                        ← Back to Demo
                    </Link>
                </div>
            </header>

            <div className="mx-auto w-full max-w-[1200px] px-6 lg:px-10 py-12 flex-1">
                {/* Step strip */}
                <div className="grid grid-cols-2 gap-3 mb-10" data-testid="gateway-steps">
                    <Step idx="01" label="PROFILE" icon={User} active={step >= 1} done={step > 1} />
                    <Step idx="02" label="STRIPE CHECKOUT" icon={ShieldCheck} active={step >= 2} done={false} />
                </div>

                <div className="grid lg:grid-cols-12 gap-6">
                    {/* Form column */}
                    <div className="lg:col-span-7 border border-white/10 bg-black p-6 lg:p-8">
                        {step === 1 && (
                            <form onSubmit={submitProfile} className="space-y-4" data-testid="profile-form">
                                <h2 className="fsrs-heading text-white text-2xl mb-2">Create your profile</h2>
                                <p className="fsrs-mono text-slate-400 text-base leading-relaxed mb-6">
                                    Profile is stored in MongoDB keyed by an anonymous client ID. <span className="text-amber-300">No real authentication yet — that&apos;s a P0 follow-up.</span>
                                </p>
                                <Field label="FULL NAME" required>
                                    <input className="fsrs-input" required value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} data-testid="profile-name" placeholder="Jane Doe, P.E." />
                                </Field>
                                <Field label="WORK EMAIL" required>
                                    <input type="email" className="fsrs-input" required value={profile.email} onChange={(e) => setProfile({ ...profile, email: e.target.value })} data-testid="profile-email" placeholder="jane@firmname.com" />
                                </Field>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="FIRM (OPTIONAL)">
                                        <input className="fsrs-input" value={profile.firm} onChange={(e) => setProfile({ ...profile, firm: e.target.value })} placeholder="Firm name" />
                                    </Field>
                                    <Field label="PE LICENSE #">
                                        <input className="fsrs-input" value={profile.license} onChange={(e) => setProfile({ ...profile, license: e.target.value })} placeholder="State + Number" />
                                    </Field>
                                </div>
                                <button type="submit" disabled={submitting} className="fsrs-cta disabled:opacity-50" data-testid="profile-submit">
                                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                                    Continue to payment
                                </button>
                            </form>
                        )}

                        {step === 2 && (
                            <form onSubmit={startCheckout} className="space-y-4" data-testid="plan-form">
                                <h2 className="fsrs-heading text-white text-2xl mb-2">Continue in Stripe Checkout</h2>
                                <div className="border border-emerald-500/40 bg-emerald-500/[0.05] p-3 mb-3">
                                    <p className="fsrs-mono text-emerald-200 text-base leading-relaxed font-bold">
                                        Billed monthly. Cancel anytime.
                                    </p>
                                </div>
                                <p className="fsrs-mono text-slate-400 text-base leading-relaxed mb-4">
                                    The plan is not active until Stripe Checkout completes. Card details stay on Stripe.
                                </p>
                                <div className="grid sm:grid-cols-2 gap-3">
                                    {PLANS.map((p) => {
                                        const active = plan === p.key;
                                        return (
                                            <button
                                                type="button"
                                                key={p.key}
                                                onClick={() => setPlan(p.key)}
                                                className={`text-left p-5 border transition-all ${active ? "border-red-500 bg-red-500/5 shadow-[inset_0_0_60px_-20px_rgba(239,68,68,0.3)]" : "border-white/15 hover:border-white/40"}`}
                                                data-testid={`plan-${p.key}`}
                                            >
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="fsrs-heading text-white text-lg">{p.label}</span>
                                                    {active && <CheckCircle2 className="w-4 h-4 text-red-500" />}
                                                </div>
                                                <div className="fsrs-mono text-white text-xl mb-1">{p.price}</div>
                                                <div className="fsrs-label text-slate-500 mb-3">{p.sub}</div>
                                                <ul className="space-y-1">
                                                    {p.bullets.map((b, i) => (
                                                        <li key={i} className="fsrs-mono text-slate-300 text-base flex items-center gap-2">
                                                            <span className="w-1 h-1 bg-red-500" /> {b}
                                                        </li>
                                                    ))}
                                                </ul>
                                            </button>
                                        );
                                    })}
                                </div>
                                <div className="flex items-center justify-between gap-3 flex-wrap pt-2">
                                    <button type="button" onClick={() => setStep(1)} className="fsrs-cta-ghost text-lg">← Back</button>
                                    <button type="submit" disabled={submitting} className="fsrs-cta disabled:opacity-50" data-testid="plan-submit">
                                        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                                        Continue to Stripe
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>

                    {/* Right sidebar — tier policy */}
                    <aside className="lg:col-span-5 border border-white/10 bg-black p-6 lg:p-8">
                        <div className="fsrs-overline mb-4">
                            /&nbsp;TIER&nbsp;POLICY
                        </div>
                        <ul className="space-y-4">
                            <PolicyItem k="FREE TIER (DEMO)" v="1 simulated demo export per lifetime · no real AI · no real uploads" tone="amber" />
                            <PolicyItem k="FOUNDING BETA · $249/month" v="Real AI · 15 SRUs included each month · lifetime founder badge" tone="emerald" />
                            <PolicyItem k="PAY-AS-YOU-GO" v="Real AI · usage-based · default $100/mo cap (configurable)" tone="emerald" />
                        </ul>
                        <div className="mt-8 pt-6 border-t border-white/10">
                            <p className="fsrs-mono text-slate-500 text-lg leading-relaxed">
                                Tier limits are enforced server-side. A plan starts only after Stripe Checkout. No card number is collected here.
                            </p>
                        </div>
                    </aside>
                </div>
            </div>

            <Footer />
            <LegalDisclaimerModal />
        </main>
    );
}

function Step({ idx, label, icon: Icon, active, done }) {
    return (
        <div className={`flex items-center gap-3 px-3 py-3 border ${active ? "border-red-500/60 bg-red-500/5" : "border-white/10 bg-black"}`}>
            <div className={`w-8 h-8 border flex items-center justify-center shrink-0 ${active ? "border-red-500" : "border-white/15"}`}>
                {done ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" strokeWidth={2} /> : <Icon className={`w-3.5 h-3.5 ${active ? "text-red-500" : "text-slate-500"}`} strokeWidth={1.5} />}
            </div>
            <div className="flex flex-col min-w-0">
                <span className="fsrs-label text-slate-500">STEP&nbsp;{idx}</span>
                <span className={`fsrs-mono text-base ${active ? "text-white" : "text-slate-400"}`}>{label}</span>
            </div>
            <span className={`ml-auto fsrs-label ${done ? "text-emerald-400" : active ? "text-red-500" : "text-slate-700"}`}>
                {done ? "OK" : active ? "•" : "—"}
            </span>
        </div>
    );
}

function PolicyItem({ k, v, tone }) {
    const c = tone === "amber" ? "text-amber-300" : tone === "emerald" ? "text-emerald-300" : "text-slate-300";
    return (
        <li className="border-l-2 border-white/15 pl-3">
            <div className={`fsrs-label ${c} font-bold mb-1`}>{k}</div>
            <div className="fsrs-mono text-slate-300 text-base leading-relaxed">{v}</div>
        </li>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="fsrs-label text-slate-400 mb-2 block">
                {label}{required && <span className="text-red-500"> *</span>}
            </span>
            {children}
        </label>
    );
}
